// Angular
import {
  Input,
  Inject,
  OnInit,
  Injector,
  Component,
  OnChanges,
  OnDestroy,
  SimpleChanges,
} from '@angular/core';
import { Router } from '@angular/router';

// NGRX
import { Store } from '@ngrx/store';

// OLCI
import * as _ from 'lodash';
import * as utils from '../../../util';
import * as fromRoot from '../../../reducers';
import * as olci from '../../../actions/olci';
import * as tracking from '../../../actions/tracking';
import { BrowserUtils } from '../../../utils/browser.utils';
import * as guestActions from '../../../actions/guest.actions';
import { AnalyticsData } from '../../../models/analytics-data';
import { MobileAppLinks } from '../../../models/mobile-app-links';
import { CONSTANTS, analyticsConstants } from '../../../constants';
import { Guest, ReservationRetrievalResponse } from '../../../models';

import { GuestService } from '../../core/services/guest.service';
import { TealiumService } from '../../core/services/tealium.service';
import { BookingService } from '../../core/services/booking.service';
import { GatingServices } from '../../core/services/gating.services';
import { AnalyticsService } from '../../core/services/analytics.services';
import { FeatureFlagsService } from '../../core/services/feature-flags.service';
import { ResourceBundleService } from '../../core/services/resource-bundle.service';
import { FeatureFlagDictionaryEnum } from 'src/app/shared/enums/feature-flag-dictionary.enum'

@Component({
    selector: 'app-landing-page', // selectforcheckin URL
    templateUrl: './landing-page.component.html',
    styleUrls: ['./landing-page.component.scss'],
    standalone: false
})
export class LandingPageComponent implements OnInit, OnChanges, OnDestroy {
  @Input() public guestsCompleted: any[] | any;
  @Input() public bundle: any;
  @Input() public checkInGuestSelectBanner: boolean | any;
  @Input() public loggedInGuest: Guest;
  @Input() public guestsNotCompleted: Guest[] | any;
  @Input() public session: ReservationRetrievalResponse;
  @Input() public selectedGuests: Guest[] | any;
  @Input() public language: any;
  @Input() public isAnyGuestLocked: boolean;
  @Input() public screensize: any = { isphone: false, isdesktop: true };
  @Input() public arrivalTimeAlert: string;
  @Input() public checkInEntryInterrupt: any;

  public myCatchedStatusErrorCodes: number[];
  public showMenu: boolean;
  public guestsCompletedGrouped: any = {};
  public guestsNotCompletedGrouped: any = {};
  public bookingIdsOfCompletedGuests: string[];
  public bookingIdsOfNotCompletedGuests: string[];
  public guests: any[] = [];
  public brandCorpAltText: string;
  public brandName: string;
  public isCubaItinerary = false;
  public cubaItineraryMessage = '';
  public customStyles: any = { message: {}, icon: {} };
  public blueBg: string;
  public exitModal;
  public brandUrl;
  public isSafari: boolean;
  public mobileAppLinks: MobileAppLinks;
  public device: string;
  public isAppleDevice: boolean;
  public isAndroidDevice: boolean;
  public deepLinkUrl: string;
  public alertMessages: any[];
  public isAppEnabled = false;
  public shouldDisplayMessageCenter = false;

  private mobileAppLinksPayload: AnalyticsData | null;

  public guestsService: GuestService;
  public featureFlagsService: FeatureFlagsService;
  public bookingService: BookingService;
  private gatingServices: GatingServices;
  public resources: ResourceBundleService;
  private browserUtils: BrowserUtils;
  private analyticsService: AnalyticsService;
  private tealiumService: TealiumService;

  public constructor(
    private injector: Injector,
    @Inject(CONSTANTS) public constants: any,
    private readonly store: Store<fromRoot.AppState>,
    private readonly router: Router,
    @Inject('UrlService') public urlService: any,
    private readonly featureFlagService: FeatureFlagsService,
  ) {
    this.injectServices();
    this.myCatchedStatusErrorCodes = [];
    this.selectedGuests = [];
    this.showMenu = false;
    this.brandName = this.bookingService.getBrand();
    this.brandCorpAltText = this.bookingService.getBrandCorpAltText();
    this.exitModal = this.constants.MODAL.EXIT;
    this.brandUrl = this.gatingServices.getBrandURL();
  }

  injectServices() {
    this.gatingServices = this.injector.get<GatingServices>(GatingServices);
    this.resources = this.injector.get<ResourceBundleService>(
      ResourceBundleService
    );
    this.browserUtils = this.injector.get<BrowserUtils>(BrowserUtils);
    this.guestsService = this.injector.get<GuestService>(GuestService);
    this.tealiumService = this.injector.get<TealiumService>(TealiumService);
    this.bookingService = this.injector.get<BookingService>(BookingService);
    this.analyticsService = this.injector.get<AnalyticsService>(AnalyticsService);
    this.featureFlagsService = this.injector.get<FeatureFlagsService>(FeatureFlagsService);
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.guestsCompletedGrouped = this.guestsService.getGroupedByBookingId(
      this.guestsCompleted
    );
    this.guestsNotCompletedGrouped = this.guestsService.getGroupedByBookingId(
      this.guestsNotCompleted
    );
    this.bookingIdsOfCompletedGuests = Object.keys(this.guestsCompletedGrouped);
    this.bookingIdsOfNotCompletedGuests = Object.keys(
      this.guestsNotCompletedGrouped
    );

    if (
      changes['checkInEntryInterrupt'] &&
      changes['checkInEntryInterrupt'].currentValue
    ) {
      this.store.dispatch(
        new olci.OpenModalAction({
          name: this.constants.MODAL.HEALTH_SAFETY_PROTOCOLS,
        })
      );
    }
  }

  public ngOnInit() {
    this.shouldDisplayMessageCenter = this.featureFlagService.snapshot[FeatureFlagDictionaryEnum.MESSAGING_CENTER];
    if (this.session && this.session.sailingClosed) {
      this.store.dispatch(
        new tracking.TrackOlciFlowEventAction({ eventType: 'sailingClosed' })
      );
    }

    const topElement = document.getElementById('first-focus-element');
    topElement?.click();
    topElement?.focus();

    const flags = this.featureFlagsService.snapshot;
    this.isAppEnabled = _.get(flags, 'appFeaturePopup', false);
    this.isCubaItinerary = this.session.cubaSailing;
    this.cubaItineraryMessage = this.resources.replacePlaceholder(
      'cuba.banner.message',
      '[url]',
      this.cubaAffidavitLink()
    );
    this.customStyles = {
      message: {
        padding: '0 0 0 20px',
        'font-size': '0.9375rem',
        'font-weight': 400,
        color: '#555F61',
        'vertical-align': 'middle',
      },
      icon: {
        width: '44px',
        height: '32px',
      },
    };
    if (this.isCubaItinerary) {
      this.dispatchTrackInlineAlert(
        'Guests traveling to Cuba must meet the requirements of one of the 12 categories of permitted travel authorized by the U.S. government. Learn more about travel requirements.'
      );
    }

    this.mobileAppLinksPayload =
      this.analyticsService.getAnalyticsDataForTracking(
        this.constants.APP_STORE_SOURCE_PAGE.MOBILE_AD_MODAL,
        this.constants.ANALYTICS_LOCATIONS.LANDING_PAGE
      );

    this.isSafari = utils.isSafari();
    this.mobileAppLinks = this.urlService.mobileAppLinks(
      this.mobileAppLinksPayload
    );
    this.device = this.browserUtils.getDevice();
    this.isAppleDevice =
      this.device === this.constants.DEVICE_TYPE.IPHONE ||
      this.device === this.constants.DEVICE_TYPE.IPAD;
    this.isAndroidDevice = this.device === this.constants.DEVICE_TYPE.ANDROID;
    this.deepLinkUrl =
      this.constants.DEEP_LINK_APP_URL[this.brandName.toUpperCase()];
    this.alertMessages = this.getAlertMessages();
  }

  public selectItem(id: any): void {
    this.store.dispatch(new guestActions.UpdateGuestToAddVM(id));
  }

  public openGovernanceContentModal(): void {
    this.store.dispatch(
      new olci.OpenModalAction({
        name: this.constants.MODAL.HEALTH_SAFETY_PROTOCOLS,
      })
    );
  }

  public canContinue(): any {
    return (
      this.guestsNotCompleted.filter((g: any) => g.selectedForCheckin).length >
      0
    );
  }

  public addAssociatedToReservation(): void {
    this.store.dispatch(new olci.ResetGuestsOnboardExpenses());
    this.router.navigate([this.constants.ROUTES.SEARCH_MINORS]);
    this.store.dispatch(
      new tracking.TrackOlciFlowEventAction({ eventType: 'addGuests' })
    );
  }

  navigateToGuestDetails() {
    const passengerIDs = this.selectedGuests.map((guest: any) => guest.guestId);
    this.store.dispatch(
      new tracking.TrackOlciStartEventAction({
        olciPassengerIds: passengerIDs.toString()
      })
    );
    this.router.navigate([this.constants.ROUTES.GUEST_DETAILS]);
  }

  public goNext(): void {
    this.store.dispatch(new olci.SetResponseErrorClearAction());
    this.navigateToGuestDetails();
  }

  public toggleShowMenu() {
    this.showMenu = !this.showMenu;
  }

  goToBrandUrl(event: any) {
    event.preventDefault();
    this.store.dispatch(
      new olci.OpenModalAction({ name: this.exitModal, url: this.brandUrl })
    );
    this.store.dispatch(
      new tracking.TrackPageInteractionEventAction('brand icon')
    );
  }

  public cubaAffidavitLink(): string {
    const url = this.urlService.cubaAffidavitLink();
    return !url || url === ''
      ? '/cruise-destinations/a-quick-guide-to-cruising-to-cuba'
      : url;
  }

  trackDeepLink(deviceStore: string) {
    const { CURRENT_PAGE } = analyticsConstants.SELECT_FOR_CHECKIN;
    const analyticsPayload = {
      event: analyticsConstants.INTERACTION_EVENT_TYPE,
      interaction: `${CURRENT_PAGE}:${deviceStore}`,
      sourcePage: this.constants.APP_STORE_SOURCE_PAGE.MOBILE_AD_MODAL,
      ctaLocation: this.constants.ANALYTICS_LOCATIONS.LANDING_PAGE,
    };
    this.store.dispatch(new tracking.TrackFrontendDeepLinkEventAction(analyticsPayload))
  }

  isMobile() {
    return (
      this.device === this.constants.DEVICE_TYPE.DESKTOP &&
      this.screensize &&
      !this.screensize.isdesktop
    );
  }

  public getAlertMessages(): any[] {
    const alerts = [];
    if (this.isAnyGuestLocked) {
      this.dispatchTrackInlineAlert(
        'Some reservations are currently unavailable.'
      );
      alerts.push(this.guestLockedAlert);
    }

    if (this.arrivalTimeAlert) {
      this.dispatchTrackInlineAlert(
        "We're not quite ready for you. Check back closer to your cruise to select an arrival time and complete check-in."
      );
      alerts.push(this.arrivalTimeEmptyInventoryAlert);
    }
    return alerts;
  }

  get guestLockedAlert() {
    return {
      closable: false,
      id: 'invalidationAlert',
      message: this.bundle ? this.bundle['banner.message.locked.booking'] : '',
      type: 'warning',
      bottom: false,
      iconUrl: '',
      customStyles: null,
    };
  }

  get arrivalTimeEmptyInventoryAlert() {
    return {
      closable: false,
      id: 'arrivalTimeAlert',
      message: this.arrivalTimeAlert,
      type: 'warning',
      bottom: false,
      iconUrl: '',
      customStyles: null,
    };
  }

  dispatchTrackInlineAlert(alertMessage: string) {
    const { CURRENT_PAGE, INTERACTION } = analyticsConstants.SELECT_FOR_CHECKIN;
    const trackingPayload = this.tealiumService.retrieveAnalyticsAlertInlineResponse(
      CURRENT_PAGE,
      INTERACTION,
      alertMessage
    );
    this.store.dispatch(new tracking.TrackInlineAlertEventAction(trackingPayload));
  }

  ngOnDestroy(): void {
    this.store.dispatch(new olci.CloseModalAction());
  }
}
